/**
 * Auction bid steps shared by the API (enforcement) and the web (display).
 * eBay's standard table, keyed on the current high bid. All amounts are cents.
 */

/** [high bid below this, step]; at $5,000.00 and up the step is $100.00. */
export const BID_INCREMENT_TIERS: ReadonlyArray<readonly [number, number]> = [
  [100, 5],
  [500, 25],
  [2_500, 50],
  [10_000, 100],
  [25_000, 250],
  [50_000, 500],
  [100_000, 1_000],
  [250_000, 2_500],
  [500_000, 5_000],
];
export const TOP_BID_INCREMENT_CENTS = 10_000;

/** The step above a high bid of `currentCents`. */
export function bidIncrementCents(currentCents: number): number {
  for (const [below, step] of BID_INCREMENT_TIERS) {
    if (currentCents < below) return step;
  }
  return TOP_BID_INCREMENT_CENTS;
}

/** The first bid an auction takes: its starting price, and at least one cent on a $0 start. */
export function openingBidCents(startPriceCents: number): number {
  return Math.max(startPriceCents, 1);
}

/** The smallest maximum a new bidder may name: the opening bid, then the high bid plus its step. */
export function minimumNextBidCents(listing: {
  startPriceCents: number | null;
  currentBidCents: number | null;
  bidCount: number;
}): number | null {
  if (listing.startPriceCents == null) return null;
  if (listing.currentBidCents == null || listing.bidCount === 0) return openingBidCents(listing.startPriceCents);
  return listing.currentBidCents + bidIncrementCents(listing.currentBidCents);
}

/** Soft close for most auctions: a bid in the last 2 minutes moves the end to 2 minutes after it. */
export const SOFT_CLOSE_WINDOW_MS = 2 * 60_000;
/** Short house auctions (under 10 minutes as listed) use a 10-second window and extension instead. */
export const SHORT_AUCTION_SOFT_CLOSE_WINDOW_MS = 10_000;
export const SHORT_AUCTION_MAX_DURATION_MS = 10 * 60_000;

/**
 * The soft-close window, which is also the extension, from the auction's
 * listed length (originalEndsAt - startsAt). Sudden death ignores it: no
 * extension at all.
 */
export function softCloseWindowMs(listing: {
  isHouse: boolean;
  startsAt: Date | string;
  originalEndsAt: Date | string | null;
}): number {
  if (!listing.isHouse || listing.originalEndsAt == null) return SOFT_CLOSE_WINDOW_MS;
  const listedMs = new Date(listing.originalEndsAt).getTime() - new Date(listing.startsAt).getTime();
  return Number.isFinite(listedMs) && listedMs < SHORT_AUCTION_MAX_DURATION_MS
    ? SHORT_AUCTION_SOFT_CLOSE_WINDOW_MS
    : SOFT_CLOSE_WINDOW_MS;
}
